import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence, useAnimationControls } from "framer-motion";
import { REGIONS, CITIES } from "@/data/cities";
import { waLink } from "@/data/site";
import { COACHING_NAME } from "@/data/coachingBrand";
import { useMotionEnabled } from "@/hooks/useMotionEnabled";
import { useMobileMenu } from "@/hooks/useMobileMenu";

const NAV = [
  { label: "Sous-location", path: "/sous-location" },
  { label: "Conciergerie", path: "/conciergerie" },
  { label: COACHING_NAME, path: "/formation-conciergerie" },
  { label: "Nos biens", path: "/nos-biens" },
];

const DESTINATIONS = Object.values(REGIONS).map((r) => ({
  ...r,
  cityList: r.cities.map((s) => CITIES[s]),
}));

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const location = useLocation();
  const motionEnabled = useMotionEnabled();
  const closeMobile = useCallback(() => setMobileOpen(false), []);
  const { panelRef, toggleRef } = useMobileMenu(mobileOpen, closeMobile);
  const menuAnimation = useAnimationControls();
  useEffect(() => {
    if (!mobileOpen) return;
    menuAnimation.stop();
    if (motionEnabled) menuAnimation.start({ opacity: 1, y: 0, transition: { duration: 0.2, ease: [0.22, 0.68, 0, 1] } });
    else menuAnimation.set({ opacity: 1, y: 0 });
  }, [mobileOpen, motionEnabled, menuAnimation]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDestOpen(false);
  }, [location.pathname, location.hash]);
  useEffect(() => {
    const close = e => { if (e.key === "Escape") { setMobileOpen(false); setDestOpen(false); } };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, []);

  const isActive = (p) => location.pathname === p || (p === "/formation-conciergerie" && ["/formation-sous-location-professionnelle", "/accompagnement-location-courte-duree"].includes(location.pathname));

  return (
    <>
      <header
        data-testid="main-navbar"
        className={`fixed top-0 left-0 right-0 z-[100] transition-colors duration-500 ${
          scrolled || mobileOpen ? "bg-[#0a0a0a]/95 backdrop-blur-md border-b border-[#1f1f1f]" : "bg-[#0a0a0a]/60 backdrop-blur-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 md:px-12 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" data-testid="logo-link" className="font-heading text-2xl md:text-[1.7rem] font-light tracking-[0.08em] text-[#f5f3ef]">
            OZC <span className="text-[#c9a96e]">IMMO</span>
          </Link>

          <nav className="hidden xl:flex items-center gap-4" aria-label="Navigation principale">
            {NAV.slice(0, 2).map((l) => (
              <Link key={l.path} to={l.path} aria-current={isActive(l.path) ? "page" : undefined} data-testid={`nav-${l.path.slice(1)}`} className={`nav-link text-[13px] tracking-[0.12em] uppercase ${isActive(l.path) ? "text-[#c9a96e]" : "text-[#b8b5ae] hover:text-[#f5f3ef]"}`}>
                {l.label}
              </Link>
            ))}

            <div className="relative" onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDestOpen(false); }}>
              <button onClick={() => setDestOpen(!destOpen)} type="button" aria-controls="destinations-menu" data-testid="nav-destinations" className="nav-link flex items-center gap-1 text-[13px] tracking-[0.12em] uppercase text-[#b8b5ae] hover:text-[#f5f3ef]" aria-expanded={destOpen}>
                Destinations <ChevronDown size={14} className={`transition-transform ${destOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {destOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: motionEnabled ? 8 : 0 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: motionEnabled ? 8 : 0 }}
                    transition={{ duration: motionEnabled ? 0.2 : 0 }}
                    data-testid="destinations-menu"
                    id="destinations-menu"
                    onClick={event => { if (event.target.closest("a[href]")) setDestOpen(false); }}
                    className="absolute top-full pt-5" style={{ left: "50%", x: "-50%" }}
                  >
                    <div className="bg-[#0e0e0e] border border-[#262626] p-8 grid grid-cols-3 gap-10 w-[640px] shadow-2xl">
                      {DESTINATIONS.map((r) => (
                        <div key={r.slug}>
                          <Link to={`/${r.slug}`} data-testid={`nav-region-${r.slug}`} className="font-heading text-xl text-[#f5f3ef] hover:text-[#c9a96e] transition-colors">{r.name}</Link>
                          <ul className="mt-4 space-y-2">
                            {r.cityList.map((c) => (
                              <li key={c.slug}>
                                <Link to={`/${c.slug}`} data-testid={`nav-city-${c.slug}`} className="text-sm text-[#8a8a8a] hover:text-[#c9a96e] transition-colors">{c.name}</Link>
                              </li>
                            ))}
                            {r.slug === "marrakech" && (
                              <>
                                <li><Link to="/marrakech#villas" data-testid="nav-marrakech-villas" className="text-sm text-[#8a8a8a] hover:text-[#c9a96e]">Villas</Link></li>
                                <li><Link to="/marrakech#riads" data-testid="nav-marrakech-riads" className="text-sm text-[#8a8a8a] hover:text-[#c9a96e]">Riads</Link></li>
                                <li><Link to="/marrakech#appartements" data-testid="nav-marrakech-appartements" className="text-sm text-[#8a8a8a] hover:text-[#c9a96e]">Appartements</Link></li>
                              </>
                            )}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {NAV.slice(2).map((l) => (
              <Link key={l.path} to={l.path} aria-current={isActive(l.path) ? "page" : undefined} data-testid={`nav-${l.path.slice(1)}`} className={`nav-link text-[13px] tracking-[0.12em] uppercase ${isActive(l.path) ? "text-[#c9a96e]" : "text-[#b8b5ae] hover:text-[#f5f3ef]"}`}>
                {l.label}
              </Link>
            ))}
            <Link to="/proprietaires" data-testid="nav-cta" className="btn-primary !py-2.5 !px-5 !text-[11px]">
              Confier mon bien
            </Link>
          </nav>

          <div className="flex items-center gap-3 xl:hidden">
            <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-whatsapp" className="text-[11px] uppercase tracking-[0.12em] text-[#c9a96e] border border-[#c9a96e]/40 px-3 py-2">
              WhatsApp
            </a>
            <button ref={toggleRef} type="button" data-testid="mobile-menu-toggle" className="motion-icon-button text-[#f5f3ef] p-2" onClick={() => setMobileOpen(!mobileOpen)} aria-expanded={mobileOpen} aria-controls="mobile-menu" aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}>
              {mobileOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            ref={panelRef}
            role="dialog" aria-modal="true" aria-label="Navigation principale"
            data-testid="mobile-menu"
            id="mobile-menu"
            onClick={event => { if (event.target.closest("a[href]")) closeMobile(); }}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0, delay: motionEnabled ? 0.2 : 0 } }}
            className="fixed inset-0 top-16 md:top-20 z-[99] bg-[#0a0a0a] overflow-y-auto xl:hidden"
          >
            <motion.div initial={{ opacity: motionEnabled ? 0 : 1, y: motionEnabled ? -10 : 0 }} animate={menuAnimation} exit={{ opacity: 0, y: motionEnabled ? -8 : 0 }} transition={{ duration: motionEnabled ? 0.2 : 0, ease: [0.22, 0.68, 0, 1] }} className="px-6 py-8 space-y-8">
              <div className="flex justify-end"><button type="button" onClick={closeMobile} data-testid="mobile-menu-close" aria-label="Fermer le menu" className="motion-icon-button inline-flex items-center gap-2 text-xs text-[#b8b5ae] p-2">Fermer <X size={16} /></button></div>
              <div className="space-y-4">
                <Link to="/" data-testid="mobile-nav-home" className="block font-heading text-3xl font-light text-[#f5f3ef]">Accueil</Link>
                {NAV.map((l) => (
                  <Link key={l.path} to={l.path} data-testid={`mobile-nav-${l.path.slice(1)}`} className={`block font-heading text-3xl font-light ${isActive(l.path) ? "text-[#c9a96e]" : "text-[#f5f3ef]"}`}>
                    {l.label}
                  </Link>
                ))}
                <Link to="/contact" data-testid="mobile-nav-contact" className="block font-heading text-3xl font-light text-[#f5f3ef]">Contact</Link>
                <Link to="/formation-sous-location-professionnelle" data-testid="mobile-nav-formation-sous-location" className="block text-sm text-[#c9a96e]">Formation sous-location professionnelle</Link>
                <Link to="/accompagnement-location-courte-duree" data-testid="mobile-nav-accompagnement-proprietaires" className="block text-sm text-[#c9a96e]">Accompagnement propriétaires</Link>
              </div>
              <div className="border-t border-[#1f1f1f] pt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
                {DESTINATIONS.map((r) => (
                  <div key={r.slug}>
                    <Link to={`/${r.slug}`} data-testid={`mobile-nav-region-${r.slug}`} className="eyebrow">{r.name}</Link>
                    <ul className="mt-3 space-y-2">
                      {r.cityList.map((c) => (
                        <li key={c.slug}><Link to={`/${c.slug}`} data-testid={`mobile-nav-city-${c.slug}`} className="text-base text-[#b8b5ae]">{c.name}</Link></li>
                      ))}
                      {r.slug === "marrakech" && ["Villas", "Riads", "Appartements"].map((s) => (
                        <li key={s}><Link to={`/marrakech#${s.toLowerCase()}`} data-testid={`mobile-nav-marrakech-${s.toLowerCase()}`} className="text-base text-[#b8b5ae]">{s}</Link></li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              <Link to="/proprietaires" data-testid="mobile-nav-cta" className="btn-primary w-full justify-center">Confier mon bien</Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
